import React, { createContext, useContext, useState, useEffect } from 'react';

interface ConfidenceToggleContextType {
  showConfidence: boolean;
  setShowConfidence: (show: boolean) => void;
}

const ConfidenceToggleContext = createContext<ConfidenceToggleContextType>({
  showConfidence: false,
  setShowConfidence: () => {},
});

export function ConfidenceToggleProvider({ children }: { children: React.ReactNode }) {
  const [showConfidence, setShowConfidence] = useState(false);

  return (
    <ConfidenceToggleContext.Provider value={{ showConfidence, setShowConfidence }}>
      {children}
    </ConfidenceToggleContext.Provider>
  );
}

/**
 * Use inside a tree that already has ConfidenceToggleProvider (global).
 * Keeps local state in sync with global: form fields and inner toggles use this,
 * so the OPD form toggle works; when global toggle changes, local syncs so form updates too.
 */
export function SyncedConfidenceToggleProvider({ children }: { children: React.ReactNode }) {
  const globalContext = useContext(ConfidenceToggleContext);
  const [localShowConfidence, setLocalShowConfidence] = useState(globalContext.showConfidence);

  useEffect(() => {
    setLocalShowConfidence(globalContext.showConfidence);
  }, [globalContext.showConfidence]);

  const setShowConfidence = (show: boolean) => {
    setLocalShowConfidence(show);
    globalContext.setShowConfidence(show);
  };

  return (
    <ConfidenceToggleContext.Provider value={{ showConfidence: localShowConfidence, setShowConfidence }}>
      {children}
    </ConfidenceToggleContext.Provider>
  );
}

export function useConfidenceToggle() {
  return useContext(ConfidenceToggleContext);
}
